import { useMemo, useState } from 'react';
import {
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { ComparisonRow } from './customerImportTypes';
import CustomerImportRow from './CustomerImportRow';

type CustomerImportTableProps = {
  rows: ComparisonRow[];
  isLoading: boolean;
  selectionMap: Record<string, boolean>;
  onToggleRow(row: ComparisonRow): void;
};

type SortColumn =
  | 'status'
  | 'customerId'
  | 'name'
  | 'businessId'
  | 'discount';

type HeaderConfig = {
  key: SortColumn;
  label: string;
};

const headers: HeaderConfig[] = [
  { key: 'status', label: 'Tila' },
  { key: 'customerId', label: 'Asiakasnumero' },
  { key: 'name', label: 'Nimi' },
  { key: 'businessId', label: 'Y-tunnus' },
  { key: 'discount', label: 'Alennus' },
];

function CustomerImportTable({
  rows,
  isLoading,
  selectionMap,
  onToggleRow,
}: CustomerImportTableProps) {
  const [sortBy, setSortBy] = useState<SortColumn>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
    'asc',
  );

  function getStatusOrder(status: ComparisonRow['status']) {
    switch (status) {
      case 'add':
        return 0;
      case 'modify':
        return 1;
      case 'remove':
        return 2;
      default:
        return 3;
    }
  }

  function getCustomerIdValue(row: ComparisonRow) {
    return row.mira?.clientId || row.sql?.customerId || '';
  }

  function getNameValue(row: ComparisonRow) {
    if (row.status === 'add' || row.status === 'modify') {
      return row.mira?.clientName ?? '';
    }
    return row.sql?.name ?? '';
  }

  function getBusinessIdValue(row: ComparisonRow) {
    if (row.status === 'add' || row.status === 'modify') {
      return row.mira?.businessId ?? '';
    }
    return row.sql?.businessId ?? '';
  }

  function getDiscountValue(row: ComparisonRow) {
    if (row.status === 'add' || row.status === 'modify') {
      return row.mira?.transportationDiscount ?? 0;
    }
    return row.sql?.discount ?? 0;
  }

  const sortedRows = useMemo(() => {
    function compareRows(a: ComparisonRow, b: ComparisonRow) {
      let result = 0;

      switch (sortBy) {
        case 'status':
          result =
            getStatusOrder(a.status) - getStatusOrder(b.status);
          break;
        case 'customerId':
          result = getCustomerIdValue(a).localeCompare(
            getCustomerIdValue(b),
          );
          break;
        case 'businessId':
          result = getBusinessIdValue(a).localeCompare(
            getBusinessIdValue(b),
          );
          break;
        case 'discount':
          result = getDiscountValue(a) - getDiscountValue(b);
          break;
        default:
          result = getNameValue(a).localeCompare(getNameValue(b));
      }

      return sortDirection === 'asc' ? result : -result;
    }
    return [...rows].sort(compareRows);
  }, [rows, sortBy, sortDirection]);

  function handleSort(column: SortColumn) {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortBy(column);
    setSortDirection('asc');
  }

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{ border: '1px solid #e0e0e0', boxSizing: 'border-box' }}
    >
      <Table size="small" sx={{ width: '100%' }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f2f2f2' }}>
            <TableCell>Valitse</TableCell>
            {headers.map((header) => (
              <TableCell key={header.key}>
                <TableSortLabel
                  active={sortBy === header.key}
                  direction={
                    sortBy === header.key ? sortDirection : 'asc'
                  }
                  onClick={() => handleSort(header.key)}
                >
                  {header.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading
            ? Array.from({ length: 6 }).map((_, index) => (
                <TableRow key={`skeleton-${index}`}>
                  {Array.from({ length: 6 }).map((__, cellIndex) => (
                    <TableCell key={`skeleton-${index}-${cellIndex}`}>
                      <Skeleton
                        variant={
                          cellIndex === 0 ? 'rectangular' : 'text'
                        }
                        height={cellIndex === 0 ? 20 : undefined}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : sortedRows.map((row) => (
                <CustomerImportRow
                  key={row.key}
                  row={row}
                  isSelected={Boolean(selectionMap[row.key])}
                  onToggleRow={onToggleRow}
                />
              ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default CustomerImportTable;
